/**
 * The ad's page while the brief is not final (docs/SPEC.md §5, §5.5, §14.1;
 * P28-T09): what the person said, the conversation and the brief card. The
 * brief's final turn ends the stage: the page becomes the film page
 * (`FilmPage`), with no button between. A failed film whose version 1 was
 * never written comes back here under its sentence and **Try again**
 * (AdPage), its card still editable: Try again writes from the edited brief.
 */
import { useState } from "react";
import type { AdDetail } from "../api/ads";
import {
  type AnswerBody,
  useAcceptSuggestion,
  useAnswerBrief,
  useEditBrief,
} from "../api/brief";
import { priceAsk, shortfallOf } from "../api/client";
import { PriceConfirm, TopUp } from "../ui/Credits";
import { DETAIL_LABELS, CARD, PAGE, say } from "../copy/brief";
import { sentenceFor } from "../copy/errors";
import { hostnameOf } from "../ui/Chips";
import { Card, Pill } from "../ui";
import { BriefCard } from "./BriefCard";
import { Conversation } from "./Conversation";

/** What the person gave at the start: the box, the details, the site, the
 *  attachments. */
function WhatYouSaid({ ad }: { ad: AdDetail }) {
  const details = Object.entries(ad.details ?? {}).filter(([, v]) => v);
  const files = ad.uploads.filter(
    (u) => u.role !== "site_logo" && u.role !== "site_picture",
  ).length;
  return (
    <Card className="flex flex-col gap-2 p-5" data-testid="what-you-said">
      <h2 className="m-0 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
        {PAGE.youSaid}
      </h2>
      {ad.text ? (
        <p className="m-0 whitespace-pre-wrap break-words text-base text-slate-900">
          {ad.text}
        </p>
      ) : (
        <p className="m-0 text-sm text-slate-500">{PAGE.nothingSaid}</p>
      )}
      {details.length > 0 ? (
        <dl className="m-0 grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_minmax(0,1fr)]">
          {details.map(([key, value]) => (
            <div key={key} className="contents">
              <dt className="text-slate-500">
                {DETAIL_LABELS[key as keyof typeof DETAIL_LABELS] ?? key}
              </dt>
              <dd className="m-0 text-slate-800">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {ad.url || files > 0 ? (
        <div className="flex flex-wrap gap-2">
          {ad.url ? (
            <Pill size="sm" tone="purple" title={ad.url}>
              {say(PAGE.site, { host: hostnameOf(ad.url) })}
            </Pill>
          ) : null}
          {files > 0 ? (
            <Pill size="sm">
              {files === 1
                ? PAGE.attachment
                : say(PAGE.attachments, { n: files })}
            </Pill>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

export function BriefStage({ ad }: { ad: AdDetail }) {
  const answer = useAnswerBrief(ad.id);
  const edit = useEditBrief(ad.id);
  const accept = useAcceptSuggestion(ad.id);
  // §15.1: an answer whose turn reaches `PRICE_CONFIRM_CREDITS` waits for the
  // person's yes to its estimate
  const [priced, setPriced] = useState<{
    body: AnswerBody;
    credits: number;
  } | null>(null);
  const sendAnswer = (body: AnswerBody, confirm = false) => {
    setPriced(null);
    answer.mutate(confirm ? { ...body, confirm: true } : body, {
      onSuccess: (made) => {
        const ask = priceAsk(made);
        if (ask) setPriced({ body, credits: ask.estimate });
      },
    });
  };
  const short = shortfallOf(answer.error);

  const conversation = ad.brief_state;
  const brief = conversation.brief ?? ad.brief;
  // the card is edited before the film: in the conversation, and in a
  // failure whose film was never written (Try again writes from it)
  const editable =
    ad.state === "brief" || (ad.state === "failed" && !ad.current_version);
  const busy = conversation.working || answer.isPending;
  // an Accept refused is said on the card, next to the line it concerns
  const answerError = answer.error ?? edit.error;

  return (
    <div
      className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]"
      data-testid="brief-stage"
    >
      <div className="flex min-w-0 flex-col gap-6">
        <WhatYouSaid ad={ad} />
        <Conversation
          state={conversation}
          sending={answer.isPending}
          onAnswer={(body) => sendAnswer(body)}
        />
        {priced ? (
          <PriceConfirm
            what="answer"
            credits={priced.credits}
            busy={answer.isPending}
            onYes={() => sendAnswer(priced.body, true)}
            onNo={() => setPriced(null)}
          />
        ) : null}
        {short ? (
          <TopUp shortfall={short} />
        ) : answerError ? (
          <p role="alert" className="m-0 text-sm text-slate-700">
            {sentenceFor(answerError)}
          </p>
        ) : null}
      </div>
      {brief ? (
        <BriefCard
          brief={brief}
          kitName={ad.brand_kit_name}
          editable={editable}
          final={conversation.final}
          busy={busy}
          onEdit={(field, value) => edit.mutateAsync({ field, value })}
          onAccept={(index, value) => accept.mutate({ index, value })}
          refused={accept.error}
          onYouDecide={() => sendAnswer({ you_decide: true })}
          dropKey={`truereel:dropped:${ad.id}`}
        />
      ) : (
        <Card
          className="p-5 text-sm text-slate-500"
          data-testid="brief-waiting"
        >
          <h2 className="m-0 mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
            {CARD.title}
          </h2>
          {CARD.waiting}
        </Card>
      )}
    </div>
  );
}
